import { FormControl } from '@angular/forms';
import { getHoursOffsetFromString, getTimestampWithTimezoneOffset, getTimezoneOffsetInHours } from '../../common/utils';
import { SECOND_IN_MS, SECONDS_IN_MIN } from '../../common/constants/date-config';

export interface SelectedTimezoneModel {
  selected: boolean;
  offset: string;
  timezone: string;
  resolved: boolean;
}

export interface LocalDate {
  timestamp: number;
  offset: string;
  timezone: string;
  resolved: boolean;
}

export class LocalDateModel implements LocalDate {
  offset: string = null;
  resolved = true;
  timestamp = 0;
  timezone: string = null;

  constructor(params?: Partial<LocalDateModel>) {
    if (params) {
      Object.assign(this, params);
    }
  }

  // subtracts timezone offset and convert timestamp to LocalDate obj before send to BE
  static getUtcDate(timestamp: Date | number, timezone: string): LocalDateModel {
    const offset = getTimezoneOffsetInHours(timezone);

    return {
      ...new LocalDateModel(),
      timestamp: getTimestampWithTimezoneOffset(+timestamp, offset),
      timezone,
    };
  }

  // wraps an already normalized timestamp into a LocalDate object for BE transport
  static toLocalDateModel(timestamp: Date | number, timezone: string): LocalDateModel {
    if (timestamp === null || timestamp === undefined) {
      return {
        ...new LocalDateModel(),
        timestamp: null,
        timezone,
      };
    }

    return {
      ...new LocalDateModel(),
      timestamp: +timestamp,
      timezone,
    };
  }

  // converts LocalDate obj to timestamp and adds timezone offset
  static getLocalDateByOffset(date: LocalDate): Date {
    const offset: number = date.offset.length === 1 ? parseInt(date.offset, 10) : getHoursOffsetFromString(date.offset);

    return new Date(getTimestampWithTimezoneOffset(+date?.timestamp, -offset));
  }

  static getLocalDate(date: LocalDate): Date {
    const offset: number = getTimezoneOffsetInHours(date.timezone);

    return new Date(getTimestampWithTimezoneOffset(+date?.timestamp, -offset));
  }

  static createLocalDateWithPredefinedOffset(timestamp: number, timezone: string, offset: string): LocalDate {
    return {
      ...new LocalDateModel(),
      timestamp,
      timezone,
      offset,
    };
  }

  static createLocalDateWithCalculatedOffset(timestamp: number, timezone: string): LocalDate {
    const offset = getTimezoneOffsetInHours(timezone).toString();
    return {
      ...new LocalDateModel(),
      timestamp,
      timezone,
      offset,
    };
  }

  static getDateAsUTCFromLocalDate(localDate: LocalDate): Date {
    const dateObj = new Date(localDate.timestamp);
    return new Date(dateObj.valueOf() + dateObj.getTimezoneOffset() * SECONDS_IN_MIN * SECOND_IN_MS);
  }
}

export class LocalDateRangeModel {
  start: LocalDate;
  end: LocalDate;

  constructor(params?: LocalDateRangeInputModel) {
    Object.assign(this, params);
  }
}

export class LocalDateRangeInputModel {
  start: Date;
  end: Date;

  constructor(params?: LocalDateRangeModel) {
    if (params) {
      const { start, end } = params;

      this.start = start?.timestamp ? LocalDateModel.getDateAsUTCFromLocalDate(start) : null;
      this.end = end?.timestamp ? LocalDateModel.getDateAsUTCFromLocalDate(end) : null;
    }
  }
}

export class LocalDateRangeOutputModel {
  start: LocalDateModel;
  end: LocalDateModel;

  constructor(params?: LocalDateRangeInputModel, timezone?: string) {
    if (params) {
      const { start, end } = params;

      this.start = start ? LocalDateModel.getUtcDate(start, timezone) : null;
      this.end = end ? LocalDateModel.getUtcDate(end, timezone) : null;
    }
  }
}

export class LocalDateFormGroup {
  constructor(
    model: LocalDate,
    public timestamp: FormControl<number> = new FormControl(model?.timestamp),
    public offset: FormControl<string> = new FormControl(model?.offset),
    public timezone: FormControl<string> = new FormControl(model?.timezone),
    public resolved: FormControl<boolean> = new FormControl(model?.resolved),
  ) {}
}
